import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cloud,
  Database,
  Users,
  Award,
  TrendingUp,
  Shield,
  Send,
  Star,
  Building2,
  Workflow,
  Zap,
} from 'lucide-react';
import useSEO from '../../hooks/useSEO';

export default function SaaSSoftware() {
  useSEO({
    title: 'SaaS Software Development | Lockie Visuals',
    description: 'Scalable cloud platforms, subscription billing and enterprise-grade SaaS solutions built to grow with your business.',
    path: '/saas-software'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  const pricingPlans = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Starter",
      description: "Perfect for startups and small businesses ready to launch their first SaaS platform.",
      price: "MK35,000",
      period: "/month",
      setupFee: "MK150,000 setup",
      features: [
        "Up to 100 active users",
        "Core feature set",
        "Email support",
        "Standard analytics",
        "Basic integrations"
      ]
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Business",
      description: "Scale your platform with advanced features, dedicated resources, and priority support.",
      price: "MK75,000",
      period: "/month",
      setupFee: "MK350,000 setup",
      features: [
        "Up to 1,000 active users",
        "Advanced feature set",
        "Priority support",
        "Custom analytics",
        "API integrations",
        "Multi-tenant ready"
      ]
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Enterprise",
      description: "Tailor-made enterprise solutions with full customization, dedicated infrastructure, and white-glove support.",
      price: "Contact Us",
      setupFee: "Custom quote",
      features: [
        "Unlimited users",
        "Full customization",
        "Dedicated infrastructure",
        "24/7 white-glove support",
        "Custom integrations",
        "SLA guarantees"
      ]
    }
  ];

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setNotification({
      type: 'success',
      title: 'Success!',
      message: "We've received your inquiry and will contact you within 24 hours."
    });
    setFormData({ name: '', email: '', service: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#021E23] transition-colors duration-300">
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: 20 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-24 right-4 p-5 rounded-2xl shadow-2xl max-w-md z-50 border backdrop-blur-xl ${notification.type === 'success'
              ? 'bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-400'
              : 'bg-red-500/10 border-red-500/20 text-red-700 dark:text-red-400'
              }`}
          >
            <div className="flex items-start gap-3">
              <div className={`p-1.5 rounded-full ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
                <Star size={14} className="text-white" />
              </div>
              <div className="space-y-1">
                <h3 className="font-bold text-sm leading-none">{notification.title}</h3>
                <p className="text-xs opacity-80 leading-relaxed font-medium">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
        <div className="relative z-10 text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 text-sm font-bold tracking-wider uppercase mb-4"
          >
            <Cloud size={16} />
            <span>Software as a Service</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight text-gray-900 dark:text-white leading-[1.1]"
          >
            SaaS Software <br />
            <span className="text-orange-500">Solutions.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed font-medium"
          >
            Build, scale, and monetize your software platform with our enterprise-grade SaaS development expertise.
            From multi-tenant architecture to subscription billing, we deliver it all.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4 pt-4"
          >
            <div className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm">
              <Zap className="w-5 h-5 text-orange-500" />
              <span className="text-[15px] font-bold text-gray-700 dark:text-gray-300 tracking-tight">Multi-tenant Ready</span>
            </div>
            <div className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm">
              <Shield className="w-5 h-5 text-orange-500" />
              <span className="text-[15px] font-bold text-gray-700 dark:text-gray-300 tracking-tight">Enterprise Security</span>
            </div>
            <div className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm">
              <Workflow className="w-5 h-5 text-orange-500" />
              <span className="text-[15px] font-bold text-gray-700 dark:text-gray-300 tracking-tight">API-first Design</span>
            </div>
          </motion.div>
        </div>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 blur-[120px] rounded-full -z-0"></div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-24 border-t border-gray-100 dark:border-white/5">
        <div className="text-center mb-16 space-y-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 dark:text-white"
          >
            Transparent <span className="text-orange-500">Pricing</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg font-medium"
          >
            Choose a plan that fits your growth stage. All prices in MK (Malawian Kwacha).
          </motion.p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group p-8 rounded-3xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 hover:border-orange-500/30 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/5 relative overflow-hidden"
            >
              <div className="p-4 bg-orange-500/10 rounded-2xl w-fit mb-6 group-hover:bg-orange-500 transition-colors duration-300 group-hover:text-white text-orange-500">
                {plan.icon}
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                {plan.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed font-medium">
                {plan.description}
              </p>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, fIdx) => (
                  <li key={fIdx} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 font-bold">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-1 pt-6 border-t border-gray-100 dark:border-white/10">
                <span className="text-orange-500 font-bold tracking-tight">{plan.price}<span className="text-xs text-gray-400 font-medium ml-1">{plan.period}</span></span>
                <span className="text-xs text-gray-500 dark:text-gray-500 font-medium">{plan.setupFee}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-4 py-24 bg-[#0B1221] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-orange-300 to-orange-500 opacity-20"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <Database size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">10+</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Projects Delivered</div>
              </div>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <Users size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">10K+</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Active Users Served</div>
              </div>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <TrendingUp size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">99.9%</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Uptime Guarantee</div>
              </div>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <Award size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">5+</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-24 whitespace-normal">
        <div className="relative rounded-2xl bg-[#0B1221] p-8 md:p-16 overflow-hidden border border-white/10">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-orange-500/10 blur-[100px] -mr-40 -mt-40"></div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center text-left">
            <div className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                Launch Your SaaS <br />
                <span className="text-orange-500">Platform.</span>
              </h2>
              <p className="text-xl text-gray-400 leading-relaxed max-w-md font-medium">
                From MVP to enterprise-scale, we design and build SaaS products that users love and businesses rely on.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                    <Cloud size={20} className="text-orange-500 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-bold uppercase tracking-wider">Call Us</p>
                    <p className="font-bold text-white">0990155300 / 0888777332</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                    <Send size={20} className="text-orange-500 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-bold uppercase tracking-wider">Email Us</p>
                    <p className="font-bold text-white">infoatlockievisuals@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white outline-none focus:border-orange-500/50 transition-colors font-bold"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white outline-none focus:border-orange-500/50 transition-colors font-bold"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Service</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleFormChange}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white outline-none focus:border-orange-500/50 transition-colors appearance-none font-bold"
                  >
                    <option value="" className="bg-[#0B1221]">Select service</option>
                    {pricingPlans.map((s, i) => (
                      <option key={i} value={s.title} className="bg-[#0B1221]">{s.title}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    required
                    rows={4}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-white outline-none focus:border-orange-500/50 transition-colors resize-none font-bold"
                    placeholder="Tell us about your SaaS project requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold uppercase tracking-widest shadow-lg shadow-orange-500/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Start SaaS Project'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
